import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw, Edit, Trash2, MoreHorizontal, Plus, FileText, ClipboardList, ShieldAlert, Activity, AlertTriangle, TrendingUp, UserPlus, LogOut, Calendar, Clock, Filter, Heart, Thermometer, Scale, Wind, Droplets } from 'lucide-react';
import { patientsApi } from '@/api/endpoints/patients.api';
import { clinicalNotesApi } from '@/api/endpoints/clinical.api';
import { riskApi } from '@/api/endpoints/risk.api';
import { careLinesApi } from '@/api/endpoints/carelines.api';
import { useAuth } from '@/contexts/AuthContext';
import type {
  RiskLevel,
  PatientMedication,
} from '@/types/patient.types';
import type { VitalSigns } from '@/types/clinical.types';
import { NoteTypeLabels, NoteStatusLabels } from '@/types/clinical.types';
import type { PatientCareLineResponse } from '@/types/careline.types';
import { RiskLevelConfig } from '@/types/risk.types';
import { CareLineStatus, CareLineStatusLabels } from '@/types/careline.types';
import { AddProblemDialog } from '@/components/features/patients/AddProblemDialog';
import { AddMedicationDialog } from '@/components/features/patients/AddMedicationDialog';
import { AddAllergyDialog } from '@/components/features/patients/AddAllergyDialog';
import { SuspendMedicationDialog } from '@/components/features/patients/SuspendMedicationDialog';
import { ReactivateMedicationDialog } from '@/components/features/patients/ReactivateMedicationDialog';
import { PatientTimeline } from '@/components/features/patients/PatientTimeline';
import { maskCPF } from '@/lib/utils';
import { ClinicalNoteCard } from '@/components/features/clinical/ClinicalNoteCard';
import { CarePlanCard } from '@/components/features/careplan';
import { RiskHistoryChart } from '@/components/features/risk/RiskHistoryChart';
import { carePlansApi } from '@/api/endpoints/careplans.api';
import { LunaChat } from '@/components/ai/LunaChat';
import { DocumentList } from '@/components/features/documents';
import { CareTeamCard } from '@/components/features/patient/CareTeamCard';
import { HealthPlansCard } from '@/components/features/patient/HealthPlansCard';
import { PatientStatusCard } from '@/components/patients/PatientStatusCard';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';

const getRiskBadge = (level?: RiskLevel) => {
  const styles: Record<RiskLevel, string> = {
    LOW: 'bg-green-100 text-green-800',
    MODERATE: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-red-100 text-red-800',
    CRITICAL: 'bg-purple-100 text-purple-800',
  };
  const labels: Record<RiskLevel, string> = {
    LOW: 'Baixo',
    MODERATE: 'Moderado',
    HIGH: 'Alto',
    CRITICAL: 'Crítico',
  };

  if (!level) {
    return (
      <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
        Não avaliado
      </span>
    );
  }

  return (
    <span className={`rounded-full px-3 py-1 text-sm font-medium ${styles[level]}`}>
      {labels[level]}
    </span>
  );
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

const getGenderLabel = (gender?: string) => {
  const labels: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
    NOT_INFORMED: 'Não informado',
  };
  return labels[gender || ''] || gender || 'Não informado';
};

const getProblemStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    ACTIVE: 'Ativo',
    RESOLVED: 'Resolvido',
    INACTIVE: 'Inativo',
  };
  return labels[status] || status;
};

const getMedicationStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    ACTIVE: 'Em uso',
    SUSPENDED: 'Suspenso',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
  };
  return labels[status] || status;
};

export default function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { isDoctor } = useAuth();
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [noteTypeFilter, setNoteTypeFilter] = useState<string>('all');
  const [noteStatusFilter, setNoteStatusFilter] = useState<string>('all');
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<PatientMedication | null>(null);

  // Care Lines dialog states
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<PatientCareLineResponse | null>(null);
  const [selectedCareLineId, setSelectedCareLineId] = useState<string>('');
  const [enrollNotes, setEnrollNotes] = useState('');
  const [dischargeReason, setDischargeReason] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isDischarging, setIsDischarging] = useState(false);

  // Query: Patient data
  const {
    data: patient,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientsApi.getById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Query: Clinical notes (lazy - only when tab is active)
  const {
    data: clinicalNotesData,
    isLoading: loadingNotes,
  } = useQuery({
    queryKey: ['patient', id, 'clinicalNotes'],
    queryFn: async () => {
      const response = await clinicalNotesApi.getByPatient(id!, { size: 20 });

      // Buscar sinais vitais em paralelo (máximo 5 notas mais recentes para otimizar)
      const notesToCheck = response.content.slice(0, 5);
      const fullNotesPromises = notesToCheck.map(note =>
        clinicalNotesApi.getById(note.id).catch(() => null)
      );

      const fullNotes = await Promise.all(fullNotesPromises);

      // Encontrar a primeira nota (mais recente) com sinais vitais
      let latestVitalSigns: VitalSigns | null = null;
      let latestVitalSignsDate: string | null = null;

      for (const fullNote of fullNotes) {
        if (fullNote?.vitalSigns && Object.keys(fullNote.vitalSigns).some(key =>
          fullNote.vitalSigns![key as keyof VitalSigns] !== undefined &&
          fullNote.vitalSigns![key as keyof VitalSigns] !== null
        )) {
          latestVitalSigns = fullNote.vitalSigns;
          latestVitalSignsDate = fullNote.createdAt;
          break;
        }
      }

      return {
        notes: response.content,
        latestVitalSigns,
        latestVitalSignsDate,
      };
    },
    enabled: !!id && activeTab === 'medical-record',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const clinicalNotes = clinicalNotesData?.notes || [];
  const latestVitalSigns = clinicalNotesData?.latestVitalSigns || null;
  const latestVitalSignsDate = clinicalNotesData?.latestVitalSignsDate || null;

  // Query: Care plans (lazy - only when tab is active)
  const {
    data: carePlans = [],
    isLoading: loadingCarePlans,
  } = useQuery({
    queryKey: ['patient', id, 'carePlans'],
    queryFn: () => carePlansApi.getByPatient(id!),
    enabled: !!id && activeTab === 'care-plans',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Query: Risk assessments (lazy - only when tab is active)
  const {
    data: riskData,
    isLoading: loadingRisk,
  } = useQuery({
    queryKey: ['patient', id, 'riskAssessments'],
    queryFn: async () => {
      const [current, history] = await Promise.all([
        riskApi.getLatestByPatient(id!),
        riskApi.getHistoryByPatient(id!),
      ]);
      return { current, history };
    },
    enabled: !!id && activeTab === 'risk',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const currentRisk = riskData?.current || null;
  const riskHistory = riskData?.history || [];

  // Query: Care lines (lazy - only when tab is active)
  const {
    data: careLinesData,
    isLoading: loadingCareLines,
  } = useQuery({
    queryKey: ['patient', id, 'careLines'],
    queryFn: async () => {
      const [enrollments, available] = await Promise.all([
        careLinesApi.getPatientEnrollments(id!),
        careLinesApi.getAllActive(),
      ]);
      return { enrollments, available };
    },
    enabled: !!id && activeTab === 'care-lines',
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const patientCareLines = careLinesData?.enrollments || [];
  const availableCareLines = careLinesData?.available || [];

  // Refresh helpers
  const refreshPatient = () => {
    queryClient.invalidateQueries({ queryKey: ['patient', id] });
  };

  const refreshCareLines = () => {
    queryClient.invalidateQueries({ queryKey: ['patient', id, 'careLines'] });
  };

  const handleProblemAdded = () => {
    refreshPatient();
  };

  const handleMedicationAdded = () => {
    refreshPatient();
  };

  const handleAllergyAdded = () => {
    refreshPatient();
  };

  const handleResolveProblem = async (problemId: string) => {
    if (!id) return;
    try {
      await patientsApi.updateProblem(id, problemId, { status: 'RESOLVED' });
      refreshPatient();
    } catch (err) {
      console.error('Erro ao resolver problema:', err);
    }
  };

  const handleDeleteProblem = async (problemId: string) => {
    if (!id || !confirm('Tem certeza que deseja excluir este problema?')) return;
    try {
      await patientsApi.deleteProblem(id, problemId);
      refreshPatient();
    } catch (err) {
      console.error('Erro ao excluir problema:', err);
    }
  };

  const handleOpenSuspendDialog = (medication: PatientMedication) => {
    setSelectedMedication(medication);
    setSuspendDialogOpen(true);
  };

  const handleOpenReactivateDialog = (medication: PatientMedication) => {
    setSelectedMedication(medication);
    setReactivateDialogOpen(true);
  };

  const handleMedicationSuspended = () => {
    refreshPatient();
  };

  const handleMedicationReactivated = () => {
    refreshPatient();
  };

  const handleDeleteMedication = async (medicationId: string) => {
    if (!id || !confirm('Tem certeza que deseja excluir este medicamento?')) return;
    try {
      await patientsApi.deleteMedication(id, medicationId);
      refreshPatient();
    } catch (err) {
      console.error('Erro ao excluir medicamento:', err);
    }
  };

  const handleDeleteAllergy = async (allergyId: string) => {
    if (!id || !confirm('Tem certeza que deseja excluir esta alergia?')) return;
    try {
      await patientsApi.deleteAllergy(id, allergyId);
      refreshPatient();
    } catch (err) {
      console.error('Erro ao excluir alergia:', err);
      alert('Erro ao excluir alergia. Tente novamente.');
    }
  };

  // Filtrar notas clínicas
  const filteredNotes = clinicalNotes.filter((note) => {
    if (noteTypeFilter !== 'all' && note.type !== noteTypeFilter) return false;
    if (noteStatusFilter !== 'all' && note.status !== noteStatusFilter) return false;
    return true;
  });

  // Care Line handlers
  const handleEnrollInCareLine = async () => {
    if (!id || !selectedCareLineId) return;
    try {
      setIsEnrolling(true);
      await careLinesApi.enrollPatient(selectedCareLineId, {
        patientId: id,
        notes: enrollNotes || undefined,
      });
      refreshCareLines();
      refreshPatient(); // Atualiza também o paciente pois careLines está no overview
      setEnrollDialogOpen(false);
      setSelectedCareLineId('');
      setEnrollNotes('');
    } catch (err) {
      console.error('Erro ao inscrever paciente:', err);
      alert('Erro ao inscrever paciente na linha de cuidado. Tente novamente.');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleDischargeFromCareLine = async () => {
    if (!selectedEnrollment || !dischargeReason) return;
    try {
      setIsDischarging(true);
      await careLinesApi.dischargePatient(selectedEnrollment.id, {
        reason: dischargeReason,
      });
      refreshCareLines();
      refreshPatient(); // Atualiza também o paciente pois careLines está no overview
      setDischargeDialogOpen(false);
      setSelectedEnrollment(null);
      setDischargeReason('');
    } catch (err) {
      console.error('Erro ao dar alta:', err);
      alert('Erro ao dar alta da linha de cuidado. Tente novamente.');
    } finally {
      setIsDischarging(false);
    }
  };

  const getAvailableCareLinesForEnrollment = () => {
    const enrolledCareLineIds = patientCareLines
      .filter((cl) => cl.status === CareLineStatus.ACTIVE)
      .map((cl) => cl.careLineId);
    return availableCareLines.filter((cl) => !enrolledCareLineIds.includes(cl.id));
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Carregando...</span>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/patients')}
            className="rounded-lg border p-2 hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">Paciente</h1>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">{error ? 'Erro ao carregar dados do paciente. Tente novamente.' : 'Paciente não encontrado'}</p>
          <button
            onClick={() => navigate('/patients')}
            className="mt-2 text-red-600 hover:underline"
          >
            Voltar para lista de pacientes
          </button>
        </div>
      </div>
    );
  }

  const activeProblems = patient.problems?.filter((p) => p.status === 'ACTIVE') || [];
  const activeMedications = patient.medications?.filter((m) => m.status === 'ACTIVE') || [];

  return (
    <div className="space-y-6">
      {/* Back button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/patients')}
          className="rounded-lg border p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-muted-foreground">Voltar para lista de pacientes</span>
      </div>

      {/* Patient Header */}
      <div className="flex items-start justify-between rounded-lg border bg-card p-6">
        <div className="flex gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
            {getInitials(patient.name)}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{patient.name}</h1>
            <p className="text-muted-foreground">
              {patient.age} anos • {getGenderLabel(patient.gender)}
            </p>
            <p className="text-sm text-muted-foreground">CPF: {maskCPF(patient.cpf)}</p>
            {patient.phone && (
              <p className="text-sm text-muted-foreground">Tel: {patient.phone}</p>
            )}
            {patient.email && (
              <p className="text-sm text-muted-foreground">{patient.email}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {getRiskBadge(patient.riskLevel)}
          <Button
            variant="outline"
            onClick={() => navigate(`/patients/${id}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4 overflow-x-auto">
          {['overview', 'medical-record', 'documents', 'care-plans', 'risk', 'care-lines', 'timeline', 'luna'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'overview' && 'Visão Geral'}
              {tab === 'medical-record' && 'Prontuário'}
              {tab === 'documents' && 'Documentos'}
              {tab === 'care-plans' && 'Planos de Cuidado'}
              {tab === 'risk' && 'Estratificação'}
              {tab === 'care-lines' && 'Linhas de Cuidado'}
              {tab === 'timeline' && 'Linha do Tempo'}
              {tab === 'luna' && '🤖 Luna IA'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-lg border bg-card p-6">
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Problemas Ativos */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold">Problemas Ativos ({activeProblems.length})</h3>
                <AddProblemDialog patientId={id!} onSuccess={handleProblemAdded} />
              </div>
              {activeProblems.length > 0 ? (
                <ul className="space-y-2">
                  {activeProblems.map((problem) => (
                    <li
                      key={problem.id}
                      className="group flex items-start justify-between rounded border p-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {problem.cidCode && (
                            <span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                              {problem.cidCode}
                            </span>
                          )}
                          <span className="font-medium">{problem.description}</span>
                        </div>
                        {problem.onsetDate && (
                          <span className="block text-xs text-muted-foreground">
                            Início: {new Date(problem.onsetDate).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {problem.notes && (
                          <span className="mt-1 block text-xs italic text-muted-foreground">
                            Obs: {problem.notes}
                          </span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleResolveProblem(problem.id)}>
                            Marcar como resolvido
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteProblem(problem.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nenhum problema ativo registrado</p>
              )}

              {/* Show resolved problems */}
              {patient.problems.filter((p) => p.status !== 'ACTIVE').length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Ver problemas resolvidos/inativos (
                    {patient.problems.filter((p) => p.status !== 'ACTIVE').length})
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {patient.problems
                      .filter((p) => p.status !== 'ACTIVE')
                      .map((problem) => (
                        <li
                          key={problem.id}
                          className="rounded border border-dashed bg-muted/50 p-2 text-muted-foreground"
                        >
                          <span className="font-medium">{problem.cidCode}</span>
                          {problem.cidCode && ' - '}
                          {problem.description}
                          <span className="ml-2 text-xs">
                            ({getProblemStatusLabel(problem.status)})
                          </span>
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>

            {/* Medicamentos */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold">Medicamentos em Uso ({activeMedications.length})</h3>
                {isDoctor ? (
                  <AddMedicationDialog patientId={id!} onSuccess={handleMedicationAdded} />
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Apenas médicos podem prescrever medicamentos">
                    <ShieldAlert className="h-3 w-3" />
                    Somente médicos
                  </span>
                )}
              </div>
              {activeMedications.length > 0 ? (
                <ul className="space-y-2">
                  {activeMedications.map((medication) => (
                    <li
                      key={medication.id}
                      className="group flex items-start justify-between rounded border p-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{medication.medicationName}</span>
                          {medication.dosage && (
                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                              {medication.dosage}
                            </span>
                          )}
                        </div>
                        {medication.activeIngredient && (
                          <span className="block text-xs text-muted-foreground">
                            Princípio ativo: {medication.activeIngredient}
                          </span>
                        )}
                        {medication.frequency && (
                          <span className="block text-sm text-muted-foreground">
                            Posologia: {medication.frequency}
                          </span>
                        )}
                        {medication.route && (
                          <span className="block text-xs text-muted-foreground">
                            Via: {medication.route}
                          </span>
                        )}
                        {medication.startDate && (
                          <span className="block text-xs text-muted-foreground">
                            Início: {new Date(medication.startDate).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {medication.notes && (
                          <span className="mt-1 block text-xs italic text-muted-foreground">
                            Obs: {medication.notes}
                          </span>
                        )}
                      </div>
                      {isDoctor ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenSuspendDialog(medication)}>
                              Suspender
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteMedication(medication.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <div
                          className="flex h-8 w-8 items-center justify-center opacity-0 group-hover:opacity-100"
                          title="Apenas médicos podem gerenciar medicamentos"
                        >
                          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nenhum medicamento em uso</p>
              )}

              {/* Show inactive medications */}
              {patient.medications.filter((m) => m.status !== 'ACTIVE').length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Ver medicamentos inativos (
                    {patient.medications.filter((m) => m.status !== 'ACTIVE').length})
                  </summary>
                  <ul className="mt-2 space-y-2">
                    {patient.medications
                      .filter((m) => m.status !== 'ACTIVE')
                      .map((medication) => (
                        <li
                          key={medication.id}
                          className="group rounded border border-dashed bg-muted/50 p-3 text-muted-foreground"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground">{medication.medicationName}</span>
                                {medication.dosage && (
                                  <span className="rounded bg-gray-200 px-2 py-0.5 text-xs">
                                    {medication.dosage}
                                  </span>
                                )}
                                <span className={`rounded px-2 py-0.5 text-xs ${
                                  medication.status === 'SUSPENDED'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {getMedicationStatusLabel(medication.status)}
                                </span>
                              </div>
                              {medication.status === 'SUSPENDED' && medication.suspendedAt && (
                                <div className="mt-1 space-y-0.5 text-xs">
                                  <span className="block">
                                    Suspenso em: {new Date(medication.suspendedAt).toLocaleDateString('pt-BR')}
                                    {medication.suspendedByName && ` por ${medication.suspendedByName}`}
                                  </span>
                                  {medication.suspendedReason && (
                                    <span className="block italic">
                                      Motivo: {medication.suspendedReason}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {medication.status === 'SUSPENDED' && isDoctor && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="opacity-0 group-hover:opacity-100"
                                onClick={() => handleOpenReactivateDialog(medication)}
                              >
                                Reativar
                              </Button>
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>

            {/* Alergias */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold">Alergias ({patient.allergies?.length || 0})</h3>
                <AddAllergyDialog patientId={id!} onSuccess={handleAllergyAdded} />
              </div>
              {patient.allergies && patient.allergies.length > 0 ? (
                <ul className="space-y-2">
                  {patient.allergies.map((allergy) => (
                    <li
                      key={allergy.id}
                      className="group flex items-start justify-between rounded border p-2"
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{allergy.allergen}</span>
                          {allergy.allergenType && (
                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                              {allergy.allergenType === 'MEDICATION' && 'Medicamento'}
                              {allergy.allergenType === 'FOOD' && 'Alimento'}
                              {allergy.allergenType === 'ENVIRONMENTAL' && 'Ambiental'}
                              {allergy.allergenType === 'OTHER' && 'Outro'}
                            </span>
                          )}
                          {allergy.severity && (
                            <span
                              className={`rounded px-2 py-0.5 text-xs ${
                                allergy.severity === 'SEVERE' ||
                                allergy.severity === 'LIFE_THREATENING'
                                  ? 'bg-red-100 text-red-800'
                                  : allergy.severity === 'MODERATE'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {allergy.severity === 'MILD' && 'Leve'}
                              {allergy.severity === 'MODERATE' && 'Moderada'}
                              {allergy.severity === 'SEVERE' && 'Grave'}
                              {allergy.severity === 'LIFE_THREATENING' && 'Risco de vida'}
                            </span>
                          )}
                          {allergy.confirmed && (
                            <span className="text-xs text-green-600">✓ Confirmada</span>
                          )}
                        </div>
                        {allergy.reaction && (
                          <span className="block text-sm text-muted-foreground mt-1">
                            Reação: {allergy.reaction}
                          </span>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDeleteAllergy(allergy.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nenhuma alergia registrada</p>
              )}
            </div>

            {/* Linhas de Cuidado */}
            <div>
              <h3 className="mb-3 font-semibold">
                Linhas de Cuidado Ativas (
                {patient.careLines?.filter((cl) => cl.status === 'ACTIVE').length || 0})
              </h3>
              {patient.careLines &&
              patient.careLines.filter((cl) => cl.status === 'ACTIVE').length > 0 ? (
                <ul className="space-y-2">
                  {patient.careLines
                    .filter((cl) => cl.status === 'ACTIVE')
                    .map((careLine) => (
                      <li
                        key={careLine.id}
                        className="group flex items-start justify-between rounded border p-2"
                      >
                        <div>
                          <span className="font-medium">{careLine.careLineName}</span>
                          <span className="block text-sm text-muted-foreground">
                            Inscrito em: {new Date(careLine.enrolledAt).toLocaleDateString('pt-BR')}
                          </span>
                          {(careLine.currentPhaseName || careLine.currentPhase) && (
                            <span className="block text-sm text-muted-foreground">
                              Fase: {careLine.currentPhaseName || careLine.currentPhase}
                            </span>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setActiveTab('care-lines')}>
                              <ClipboardList className="mr-2 h-4 w-4" />
                              Ver detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                // Buscar o enrollment completo para usar no dialog de alta
                                let enrollment = patientCareLines.find(
                                  (pcl) => pcl.id === careLine.id
                                );
                                if (!enrollment) {
                                  // Se não encontrou no cache, buscar do servidor
                                  const enrollments = await careLinesApi.getPatientEnrollments(id!);
                                  enrollment = enrollments.find((e) => e.id === careLine.id);
                                  // Invalida o cache para próxima consulta ter dados atualizados
                                  refreshCareLines();
                                }
                                if (enrollment) {
                                  setSelectedEnrollment(enrollment);
                                  setDischargeDialogOpen(true);
                                }
                              }}
                              className="text-destructive"
                            >
                              <LogOut className="mr-2 h-4 w-4" />
                              Dar alta
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Não inscrito em linhas de cuidado</p>
              )}
            </div>

            {/* Status do Paciente / Inativação */}
            <PatientStatusCard patient={patient} />

            {/* Equipe de Cuidado */}
            <CareTeamCard patientId={id!} />

            {/* Planos de Saúde / Carteirinhas */}
            <HealthPlansCard patientId={id!} />
          </div>
        )}

        {activeTab === 'medical-record' && (
          <div className="space-y-4">
            {/* Card de Últimos Sinais Vitais */}
            {latestVitalSigns && (
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    Últimos Sinais Vitais
                  </h3>
                  {latestVitalSignsDate && (
                    <span className="text-sm text-muted-foreground">
                      {new Date(latestVitalSignsDate).toLocaleDateString('pt-BR')} às{' '}
                      {new Date(latestVitalSignsDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {(latestVitalSigns.systolicBP || latestVitalSigns.diastolicBP) && (
                    <div className="flex items-center gap-3">
                      <Heart className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Pressão Arterial</p>
                        <p className="font-semibold">
                          {latestVitalSigns.systolicBP}x{latestVitalSigns.diastolicBP} mmHg
                        </p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.heartRate && (
                    <div className="flex items-center gap-3">
                      <Activity className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Frequência Cardíaca</p>
                        <p className="font-semibold">{latestVitalSigns.heartRate} bpm</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.respiratoryRate && (
                    <div className="flex items-center gap-3">
                      <Wind className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Frequência Respiratória</p>
                        <p className="font-semibold">{latestVitalSigns.respiratoryRate} rpm</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.temperature && (
                    <div className="flex items-center gap-3">
                      <Thermometer className="h-5 w-5 text-orange-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Temperatura</p>
                        <p className="font-semibold">{latestVitalSigns.temperature} °C</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.oxygenSaturation && (
                    <div className="flex items-center gap-3">
                      <Droplets className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">SpO2</p>
                        <p className="font-semibold">{latestVitalSigns.oxygenSaturation}%</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.weight && (
                    <div className="flex items-center gap-3">
                      <Scale className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Peso</p>
                        <p className="font-semibold">{latestVitalSigns.weight} kg</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.height && (
                    <div className="flex items-center gap-3">
                      <Scale className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Altura</p>
                        <p className="font-semibold">{latestVitalSigns.height} cm</p>
                      </div>
                    </div>
                  )}
                  {latestVitalSigns.bmi && (
                    <div className="flex items-center gap-3">
                      <Scale className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">IMC</p>
                        <p className="font-semibold">{latestVitalSigns.bmi.toFixed(1)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cabeçalho com filtros */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-semibold">Evoluções Clínicas</h3>
              <div className="flex flex-wrap items-center gap-2">
                {/* Filtro por tipo */}
                <Select value={noteTypeFilter} onValueChange={setNoteTypeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {Object.entries(NoteTypeLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Filtro por status */}
                <Select value={noteStatusFilter} onValueChange={setNoteStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos status</SelectItem>
                    {Object.entries(NoteStatusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button onClick={() => navigate(`/clinical/new?patientId=${id}`)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nova Evolução
                </Button>
              </div>
            </div>

            {loadingNotes ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredNotes.length > 0 ? (
              <div className="space-y-3">
                {filteredNotes.map((note) => (
                  <ClinicalNoteCard
                    key={note.id}
                    note={note}
                    showPatientName={false}
                    onClick={() => navigate(`/clinical/${note.id}?from=patient`)}
                  />
                ))}
                {clinicalNotes.length >= 10 && (
                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/clinical?patientId=${id}`)}
                    >
                      Ver todas as evoluções
                    </Button>
                  </div>
                )}
              </div>
            ) : clinicalNotes.length > 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Filter className="h-12 w-12 text-muted-foreground" />
                <h4 className="mt-4 font-semibold">Nenhuma evolução encontrada</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tente ajustar os filtros para ver mais resultados
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setNoteTypeFilter('all');
                    setNoteStatusFilter('all');
                  }}
                >
                  Limpar filtros
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground" />
                <h4 className="mt-4 font-semibold">Nenhuma evolução registrada</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  Clique em "Nova Evolução" para criar o primeiro registro clínico
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <DocumentList patientId={id!} showUpload={true} />
        )}

        {activeTab === 'care-plans' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Planos de Cuidado</h3>
              <Button onClick={() => navigate(`/care-plans/new?patientId=${id}`)}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Plano
              </Button>
            </div>

            {loadingCarePlans ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : carePlans.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {carePlans.map((plan) => (
                  <CarePlanCard
                    key={plan.id}
                    carePlan={plan}
                    onClick={() => navigate(`/care-plans/${plan.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ClipboardList className="h-12 w-12 text-muted-foreground" />
                <h4 className="mt-4 font-semibold">Nenhum plano de cuidado</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  Clique em "Novo Plano" para criar o primeiro plano de cuidado
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'risk' && (
          <div className="space-y-6">
            {/* Header com botão de nova avaliação */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Estratificação de Risco</h3>
              <Button onClick={() => navigate(`/risk/new?patientId=${id}`)}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Avaliação
              </Button>
            </div>

            {loadingRisk ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Avaliação atual */}
                {currentRisk ? (
                  <div className="rounded-lg border bg-card p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <h4 className="font-semibold">Avaliação Atual</h4>
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-full px-3 py-1 text-sm font-medium"
                          style={{
                            backgroundColor: RiskLevelConfig[currentRisk.overallLevel].bgColor,
                            color: RiskLevelConfig[currentRisk.overallLevel].color,
                          }}
                        >
                          {RiskLevelConfig[currentRisk.overallLevel].label}
                        </span>
                        {currentRisk.isExpired && (
                          <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800">
                            <AlertTriangle className="h-3 w-3" />
                            Expirada
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Scores */}
                    <div className="mb-6 grid gap-4 md:grid-cols-4">
                      <div className="rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Activity className="h-4 w-4" />
                          <span className="text-sm">Score Clínico</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-2xl font-bold">{currentRisk.clinicalScore}</span>
                          <span className="text-muted-foreground">/100</span>
                        </div>
                        <Progress value={currentRisk.clinicalScore} className="mt-2" />
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Activity className="h-4 w-4" />
                          <span className="text-sm">Score Psicossocial</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-2xl font-bold">{currentRisk.psychosocialScore}</span>
                          <span className="text-muted-foreground">/100</span>
                        </div>
                        <Progress value={currentRisk.psychosocialScore} className="mt-2" />
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <TrendingUp className="h-4 w-4" />
                          <span className="text-sm">Risco de Internação</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-2xl font-bold">{currentRisk.hospitalizationRisk}</span>
                          <span className="text-muted-foreground">%</span>
                        </div>
                        <Progress value={currentRisk.hospitalizationRisk} className="mt-2" />
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-sm">Risco Uso PA</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-2xl font-bold">{currentRisk.erUsageRisk}</span>
                          <span className="text-muted-foreground">%</span>
                        </div>
                        <Progress value={currentRisk.erUsageRisk} className="mt-2" />
                      </div>
                    </div>

                    {/* Fatores de risco */}
                    {currentRisk.factors && currentRisk.factors.length > 0 && (
                      <div className="mb-4">
                        <h5 className="mb-2 font-medium">Fatores de Risco Identificados</h5>
                        <div className="flex flex-wrap gap-2">
                          {currentRisk.factors.map((factor, index) => (
                            <span
                              key={index}
                              className="rounded-full bg-red-50 px-3 py-1 text-sm text-red-700"
                              title={`Peso: ${factor.weight}`}
                            >
                              {factor.description}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Metadados */}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Avaliado em: {new Date(currentRisk.assessedAt).toLocaleDateString('pt-BR')}
                      </span>
                      {currentRisk.assessedByName && (
                        <span>Por: {currentRisk.assessedByName}</span>
                      )}
                      {currentRisk.validUntil && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          Válido até: {new Date(currentRisk.validUntil).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>

                    {currentRisk.notes && (
                      <div className="mt-4 rounded bg-muted p-3">
                        <p className="text-sm italic">{currentRisk.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-lg border bg-card py-12 text-center">
                    <Activity className="h-12 w-12 text-muted-foreground" />
                    <h4 className="mt-4 font-semibold">Nenhuma avaliação de risco</h4>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Clique em "Nova Avaliação" para realizar a primeira estratificação
                    </p>
                  </div>
                )}

                {/* Histórico com Gráfico de Tendência */}
                {riskHistory.length > 1 && (
                  <div className="rounded-lg border bg-card p-6">
                    <h4 className="mb-4 text-lg font-semibold">Histórico de Avaliações</h4>
                    <RiskHistoryChart
                      history={riskHistory}
                      onSelectAssessment={(assessmentId) => navigate(`/risk/${assessmentId}`)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'care-lines' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Linhas de Cuidado</h3>
              {getAvailableCareLinesForEnrollment().length > 0 && (
                <Button onClick={() => setEnrollDialogOpen(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Inscrever em Linha de Cuidado
                </Button>
              )}
            </div>

            {loadingCareLines ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Linhas de cuidado ativas */}
                {patientCareLines.filter((cl) => cl.status === CareLineStatus.ACTIVE).length > 0 ? (
                  <div>
                    <h4 className="mb-3 font-medium text-muted-foreground">Inscrições Ativas</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      {patientCareLines
                        .filter((cl) => cl.status === CareLineStatus.ACTIVE)
                        .map((enrollment) => (
                          <div
                            key={enrollment.id}
                            className="rounded-lg border bg-card p-4"
                          >
                            <div className="mb-3 flex items-start justify-between">
                              <h5 className="font-semibold">{enrollment.careLineName}</h5>
                              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                Ativo
                              </span>
                            </div>

                            <div className="mb-3 space-y-1 text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="h-4 w-4" />
                                Inscrito em: {new Date(enrollment.enrolledAt).toLocaleDateString('pt-BR')}
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                {enrollment.daysEnrolled} dias na linha de cuidado
                              </div>
                              {(enrollment.currentPhaseName || enrollment.currentPhase) && (
                                <div className="text-muted-foreground">
                                  Fase: <span className="font-medium text-foreground">{enrollment.currentPhaseName || enrollment.currentPhase}</span>
                                </div>
                              )}
                            </div>

                            {enrollment.notes && (
                              <p className="mb-3 text-sm italic text-muted-foreground">
                                {enrollment.notes}
                              </p>
                            )}

                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedEnrollment(enrollment);
                                  setDischargeDialogOpen(true);
                                }}
                              >
                                <LogOut className="mr-1 h-3 w-3" />
                                Dar Alta
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-lg border bg-card py-12 text-center">
                    <ClipboardList className="h-12 w-12 text-muted-foreground" />
                    <h4 className="mt-4 font-semibold">Não inscrito em linhas de cuidado</h4>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {getAvailableCareLinesForEnrollment().length > 0
                        ? 'Clique em "Inscrever em Linha de Cuidado" para iniciar o acompanhamento'
                        : 'Nenhuma linha de cuidado disponível no momento'}
                    </p>
                  </div>
                )}

                {/* Histórico de linhas de cuidado (altas) */}
                {patientCareLines.filter((cl) => cl.status !== CareLineStatus.ACTIVE).length > 0 && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                      Ver histórico de linhas de cuidado ({patientCareLines.filter((cl) => cl.status !== CareLineStatus.ACTIVE).length})
                    </summary>
                    <div className="mt-3 space-y-2">
                      {patientCareLines
                        .filter((cl) => cl.status !== CareLineStatus.ACTIVE)
                        .map((enrollment) => (
                          <div
                            key={enrollment.id}
                            className="flex items-center justify-between rounded border border-dashed bg-muted/50 p-3"
                          >
                            <div>
                              <span className="font-medium">{enrollment.careLineName}</span>
                              <span className="ml-2 rounded bg-gray-200 px-2 py-0.5 text-xs">
                                {CareLineStatusLabels[enrollment.status]}
                              </span>
                              {enrollment.dischargeReason && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Motivo: {enrollment.dischargeReason}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              <div>
                                Inscrito: {new Date(enrollment.enrolledAt).toLocaleDateString('pt-BR')}
                              </div>
                              {enrollment.dischargedAt && (
                                <div>
                                  Alta: {new Date(enrollment.dischargedAt).toLocaleDateString('pt-BR')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'timeline' && <PatientTimeline patientId={id!} />}

        {activeTab === 'luna' && (
          <div className="flex justify-center">
            <LunaChat
              patientId={id!}
              patientName={patient.name}
              className="w-full max-w-2xl"
            />
          </div>
        )}
      </div>

      {/* Dialogs de medicamento */}
      {selectedMedication && (
        <>
          <SuspendMedicationDialog
            patientId={id!}
            medication={selectedMedication}
            open={suspendDialogOpen}
            onOpenChange={setSuspendDialogOpen}
            onSuccess={handleMedicationSuspended}
          />
          <ReactivateMedicationDialog
            patientId={id!}
            medication={selectedMedication}
            open={reactivateDialogOpen}
            onOpenChange={setReactivateDialogOpen}
            onSuccess={handleMedicationReactivated}
          />
        </>
      )}

      {/* Dialog de inscrição em linha de cuidado */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inscrever em Linha de Cuidado</DialogTitle>
            <DialogDescription>
              Selecione uma linha de cuidado para inscrever o paciente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="careLine">Linha de Cuidado</Label>
              <Select value={selectedCareLineId} onValueChange={setSelectedCareLineId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma linha de cuidado" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableCareLinesForEnrollment().map((careLine) => (
                    <SelectItem key={careLine.id} value={careLine.id}>
                      {careLine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Textarea
                id="notes"
                value={enrollNotes}
                onChange={(e) => setEnrollNotes(e.target.value)}
                placeholder="Observações sobre a inscrição..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEnrollInCareLine}
              disabled={!selectedCareLineId || isEnrolling}
            >
              {isEnrolling ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Inscrevendo...
                </>
              ) : (
                'Inscrever'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de alta de linha de cuidado */}
      <Dialog open={dischargeDialogOpen} onOpenChange={setDischargeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dar Alta da Linha de Cuidado</DialogTitle>
            <DialogDescription>
              {selectedEnrollment && (
                <>Paciente será removido da linha de cuidado "{selectedEnrollment.careLineName}".</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dischargeReason">Motivo da Alta *</Label>
              <Select value={dischargeReason} onValueChange={setDischargeReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Objetivos alcançados">Objetivos alcançados</SelectItem>
                  <SelectItem value="Melhora clínica significativa">Melhora clínica significativa</SelectItem>
                  <SelectItem value="Transferência de cuidado">Transferência de cuidado</SelectItem>
                  <SelectItem value="Desistência do paciente">Desistência do paciente</SelectItem>
                  <SelectItem value="Mudança de plano de saúde">Mudança de plano de saúde</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDischargeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDischargeFromCareLine}
              disabled={!dischargeReason || isDischarging}
              variant="destructive"
            >
              {isDischarging ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Confirmar Alta'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
